/**
 * unitConversion.js
 *
 * mm ↔ inch primitives + sandvik catalog metric/imperial field mapping
 * for the MRT PWA wizard (D5 — hole-diameter slider unit toggle) and
 * future PLP / detail-page consumers.
 *
 * The scalar primitives (`mmToInches`, `inchesToMm`, `formatInchesFraction`)
 * are NEW for MRT — the sandvik catalog source util only exposes
 * field-mapping helpers (`getFieldValue`, `getUnitLabel`, …) for products
 * that already carry pre-converted imperial fields. The wizard slider
 * stores a single mm canonical value and needs to render display fractions
 * at arbitrary millimetre points, so the scalar conversions are added
 * here. The field-mapping helpers are ported verbatim so the catalog
 * pattern (`getFieldValue(product, 'diameterMm', 'imperial')`) drops in
 * unchanged when PLP work begins.
 *
 * Pure / side-effect-free. No React, no DOM, no external imports.
 *
 * @module utils/unitConversion
 * @since 2026-05-17 (Group D Sub-Plan 2, Task D5)
 */

// ---------------------------------------------------------------------------
// Scalar primitives (NEW for MRT)
// ---------------------------------------------------------------------------

/** Exact conversion factor — 1 inch = 25.4 mm (international inch, ISO 31-1). */
export const MM_PER_INCH = 25.4;

/**
 * Convert millimetres to decimal inches.
 *
 * @param {number} mm
 * @returns {number} inches, rounded to 3 decimal places (e.g. 89 → 3.504).
 *   Returns NaN if input is not finite.
 */
export function mmToInches(mm) {
  if (!Number.isFinite(mm)) return NaN;
  return Math.round((mm / MM_PER_INCH) * 1000) / 1000;
}

/**
 * Convert decimal inches to millimetres.
 *
 * @param {number} inches
 * @returns {number} millimetres, rounded to 2 decimal places.
 *   Returns NaN if input is not finite.
 */
export function inchesToMm(inches) {
  if (!Number.isFinite(inches)) return NaN;
  return Math.round(inches * MM_PER_INCH * 100) / 100;
}

/**
 * Render a millimetre value as a Sandvik-convention inch fraction string,
 * e.g. 89 mm → `3 1/2"`, 25 mm → `1"`, 152 mm → `6"`.
 *
 * Algorithm: convert to inches, then round to the nearest 1/16. Reduce the
 * fraction by GCD, and emit:
 *   - whole part only if remainder is 0
 *   - improper handling: if remainder rounds up to 16/16, carry to whole
 *   - bare whole when fraction is 0/16
 *   - whole + fraction otherwise
 *
 * The 1/16 step matches the catalog's bitDiameterInchDisplay precision
 * for top-hammer bits (audited 17 May 2026: all TH bit imperial labels
 * in products-tophammer.json are expressible at 1/16 granularity).
 *
 * @param {number} mm
 * @returns {string} e.g. `'3 1/2"'`, `'6"'`, `'7/8"'`. Empty string if NaN.
 */
export function formatInchesFraction(mm) {
  if (!Number.isFinite(mm)) return '';
  const totalSixteenths = Math.round((mm / MM_PER_INCH) * 16);
  let whole = Math.floor(totalSixteenths / 16);
  let sixteenths = totalSixteenths - whole * 16;

  if (sixteenths === 0) return `${whole}"`;

  // Reduce by GCD to get the smallest-denominator fraction.
  const g = gcd(sixteenths, 16);
  const num = sixteenths / g;
  const den = 16 / g;

  if (whole === 0) return `${num}/${den}"`;
  return `${whole} ${num}/${den}"`;
}

function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) { [a, b] = [b, a % b]; }
  return a;
}

// ---------------------------------------------------------------------------
// Catalog metric/imperial field mapping
// (ported verbatim from sandvik-catalog/src/utils/unitConversion.js so PLP
// work in Sub-Plan 3 / 4 drops in without divergence)
// ---------------------------------------------------------------------------

/** Mapping of metric field names to their imperial equivalents. */
export const METRIC_TO_IMPERIAL = {
  diameterMm: 'diameterInch',
  bitDiameterMm: 'bitDiameterInch',
  lengthMm: 'lengthInch',
  dimensionLengthMm: 'dimensionLengthInch',
  taperedRodsLengthMm: 'taperedRodsLengthInch',
  weightKg: 'weightLb',
  rodDimension: 'rodDimensionInch',
  fishingPikeDiameterMm: 'fishingPikeDiameterInch',
};

/** Unit labels for each field type. */
export const FIELD_UNITS = {
  diameterMm: { metric: 'mm', imperial: 'in' },
  diameterInch: { metric: 'mm', imperial: 'in' },
  bitDiameterMm: { metric: 'mm', imperial: 'in' },
  bitDiameterInch: { metric: 'mm', imperial: 'in' },
  lengthMm: { metric: 'mm', imperial: 'in' },
  lengthInch: { metric: 'mm', imperial: 'in' },
  dimensionLengthMm: { metric: 'mm', imperial: 'in' },
  dimensionLengthInch: { metric: 'mm', imperial: 'in' },
  taperedRodsLengthMm: { metric: 'mm', imperial: 'in' },
  weightKg: { metric: 'kg', imperial: 'lb' },
  weightLb: { metric: 'kg', imperial: 'lb' },
  rodDimension: { metric: 'mm', imperial: 'in' },
};

/**
 * Get the correct field value based on unit preference.
 *
 * @param {Object} product
 * @param {string} fieldName - metric field name (e.g. 'diameterMm')
 * @param {'metric'|'imperial'} units
 * @returns {*} imperial value if requested and available; metric otherwise.
 */
export function getFieldValue(product, fieldName, units = 'metric') {
  if (!product) return null;
  if (units === 'imperial') {
    const imperialField = METRIC_TO_IMPERIAL[fieldName];
    if (imperialField && product[imperialField] != null && product[imperialField] !== '') {
      return product[imperialField];
    }
  }
  return product[fieldName];
}

/**
 * Get the unit label for a field.
 *
 * @param {string} fieldName
 * @param {'metric'|'imperial'} units
 * @returns {string}
 */
export function getUnitLabel(fieldName, units = 'metric') {
  const config = FIELD_UNITS[fieldName];
  if (!config) return '';
  return units === 'imperial' ? config.imperial : config.metric;
}

/**
 * Format a dimension value with its unit (e.g. '38 mm', '1.5 in').
 *
 * @param {*} value
 * @param {string} fieldName
 * @param {'metric'|'imperial'} units
 * @returns {string}
 */
export function formatDimension(value, fieldName, units = 'metric') {
  if (value == null || value === '') return '—';
  const unit = getUnitLabel(fieldName, units);
  return unit ? `${value} ${unit}` : String(value);
}

/** Check whether a field is a measurement field. */
export function isDimensionField(fieldName) {
  return fieldName in FIELD_UNITS || fieldName in METRIC_TO_IMPERIAL;
}
